import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, PawPrint, ChevronLeft, X } from "lucide-react";
import { Loading } from "@/components/pawkt/Empty";

const ALL = "__all__";
const plural = (n, w) => `${n.toLocaleString("en-IN")} ${w}${n === 1 ? "" : "s"}`;
const input = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10";

function ageParams(min, max) {
  const p = new URLSearchParams();
  if (min !== "") p.set("min_age", min);
  if (max !== "") p.set("max_age", max);
  return p;
}

function BreedList({ data, onPick }) {
  if (!data.items.length) return <div className="py-10 text-center text-sm text-slate-400" data-testid="breed-empty">No breeds match these filters.</div>;
  return (
    <div className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="breed-list">
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span>Breed</span><span className="w-10 text-right sm:w-16">Pets</span><span className="w-24 text-right sm:w-32">Age range</span>
      </div>
      {data.items.map((b) => (
        <button key={b.breed_key} onClick={() => onPick(b)} data-testid={`breed-row-${b.breed_key.replace(/\s+/g, "-")}`}
          className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-emerald-50/60 focus-visible:bg-emerald-50 focus-visible:outline-none">
          <span className="font-medium text-slate-800 break-words">{b.breed}</span>
          <span className="w-10 text-right font-semibold tabular text-slate-900 sm:w-16">{b.pet_count}</span>
          <span className="w-24 text-right text-slate-600 sm:w-32">{b.age_range_label}
            {b.unknown_age_count > 0 && b.known_age_count > 0 && <span className="block text-[11px] text-slate-400">{b.unknown_age_count} age unknown</span>}</span>
        </button>
      ))}
    </div>
  );
}

function BreedPets({ breed, min, max, onBack }) {
  const navigate = useNavigate();
  const p = ageParams(min, max);
  p.set("in_care", "1"); p.set("breed", breed.breed_key); p.set("limit", "200");
  const q = useQuery({ queryKey: ["breed-pets", p.toString()], queryFn: async () => (await api.get(`/pets?${p}`)).data });
  return (
    <div data-testid="breed-pets">
      <button onClick={onBack} data-testid="breed-back" className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><ChevronLeft className="h-3.5 w-3.5" /> All breeds</button>
      {q.isLoading ? <Loading /> : q.isError ? <div className="text-sm text-rose-600">{apiErr(q.error)}</div> : (
        <>
          <div className="mb-2 text-sm text-slate-600" data-testid="breed-pets-count"><b>{plural(q.data.total, "pet")}</b> · {breed.breed}{q.data.total > q.data.items.length ? ` (showing first ${q.data.items.length})` : ""}</div>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {q.data.items.map((pet) => (
              <div key={pet.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <button onClick={() => navigate(`/pets/${pet.id}`)} data-testid={`breed-pet-${pet.id}`} className="flex min-w-0 flex-1 items-center gap-3 text-left hover:text-emerald-700">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50"><PawPrint className="h-4 w-4 text-emerald-600" /></span>
                  <span className="min-w-0"><span className="block font-medium text-slate-800 truncate">{pet.name}</span>
                    <span className="block text-xs text-slate-400 truncate">{[pet.breed, pet.species, pet.gender].filter(Boolean).join(" · ")}</span></span>
                </button>
                <span className="w-20 text-right text-slate-600 sm:w-24">{pet.age_label}
                  {pet.owner_name && <button onClick={() => navigate(`/customers/${pet.customer_id}`)} data-testid={`breed-pet-owner-m-${pet.id}`}
                    className="block w-full truncate text-right text-[11px] text-slate-500 hover:underline sm:hidden">{pet.owner_name}</button>}</span>
                {pet.owner_name ? <button onClick={() => navigate(`/customers/${pet.customer_id}`)} data-testid={`breed-pet-owner-${pet.id}`}
                  className="hidden w-36 truncate text-right text-xs text-slate-500 hover:text-emerald-700 hover:underline sm:block">{pet.owner_name}</button> : <span className="hidden w-36 sm:block" />}
              </div>
            ))}
            {!q.data.items.length && <div className="py-8 text-center text-sm text-slate-400">No pets match these filters.</div>}
          </div>
        </>
      )}
    </div>
  );
}

export function BreedAnalytics({ onClose }) {
  const [search, setSearch] = useState("");
  const [breed, setBreed] = useState(ALL);
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [picked, setPicked] = useState(null);
  const p = ageParams(min, max);
  if (search) p.set("search", search);
  const q = useQuery({ queryKey: ["pet-breeds", p.toString()], queryFn: async () => (await api.get(`/pets/breeds?${p}`)).data });
  const all = useQuery({ queryKey: ["pet-breeds", ""], queryFn: async () => (await api.get(`/pets/breeds`)).data });
  const ageBad = min !== "" && max !== "" && Number(min) > Number(max);
  const shown = q.data ? { ...q.data, items: breed === ALL ? q.data.items : q.data.items.filter((b) => b.breed_key === breed) } : null;
  const matchCount = shown ? shown.items.reduce((s, b) => s + b.pet_count, 0) : 0;
  const clear = () => { setSearch(""); setBreed(ALL); setMin(""); setMax(""); setPicked(null); };
  const ageInput = (v, set, id, ph) => <input type="number" min={0} max={40} inputMode="numeric" value={v} placeholder={ph} data-testid={id} aria-label={ph}
    onChange={(e) => { set(e.target.value === "" ? "" : String(Math.max(0, Math.min(40, parseInt(e.target.value, 10) || 0)))); setPicked(null); }} className={input} />;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="breed-analytics">
        <DialogHeader><DialogTitle className="font-display">Breed analytics</DialogTitle></DialogHeader>
        <p className="-mt-2 text-xs text-slate-400">Pets in care, grouped by breed. Ages (in years) come from date of birth, or from the age entered when no date of birth is known; pets with neither are counted as age unknown.</p>
        <div className="grid gap-2 sm:grid-cols-[1.4fr_1.2fr_0.7fr_0.7fr_auto]">
          <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPicked(null); }} placeholder="Search breed…" data-testid="breed-search" className={`${input} pl-9`} /></div>
          <Select value={breed} onValueChange={(v) => { setBreed(v); setPicked(null); }}>
            <SelectTrigger className="bg-white" data-testid="breed-filter"><SelectValue placeholder="All breeds" /></SelectTrigger>
            <SelectContent className="bg-white max-h-64">
              <SelectItem value={ALL}>All breeds</SelectItem>
              {(all.data?.items || []).map((b) => <SelectItem key={b.breed_key} value={b.breed_key}>{b.breed}</SelectItem>)}
            </SelectContent>
          </Select>
          {ageInput(min, setMin, "breed-min-age", "Min age")}
          {ageInput(max, setMax, "breed-max-age", "Max age")}
          <Button variant="outline" onClick={clear} data-testid="breed-clear" className="gap-1"><X className="h-3.5 w-3.5" /> Clear</Button>
        </div>
        {ageBad ? <div className="text-sm text-amber-700" data-testid="breed-age-error">Minimum age can't be more than maximum age.</div>
          : q.isLoading ? <Loading /> : q.isError ? <div className="text-sm text-rose-600" data-testid="breed-error">Couldn't load breed analytics. {apiErr(q.error)}</div>
          : picked ? <BreedPets breed={picked} min={min} max={max} onBack={() => setPicked(null)} />
          : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600" data-testid="breed-result-count">
                <span><b>{plural(matchCount, "pet")}</b> match · {plural(shown.items.length, "breed")}</span>
                {(min !== "" || max !== "") && <span className="text-xs text-slate-400">Age filters include only pets with a known date of birth</span>}
                {shown.breed_not_recorded > 0 && breed === ALL && <span className="text-xs text-slate-400">{plural(shown.breed_not_recorded, "pet")} without a breed recorded</span>}
              </div>
              <BreedList data={shown} onPick={setPicked} />
            </>
          )}
      </DialogContent>
    </Dialog>
  );
}
